import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { AccordionAnimation } from "components/AccordionAnimation";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Loader from "components/DesignSystem/Loader/loader";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { DEFAULT_PAGE_SIZE } from "constants/base";
import { useQuery } from "hooks/useQuery";
import { GeneralLedgerTable } from "components/GeneralLedger/GeneralLedgerAccordion/GeneralLedgerAccordion";
import { ArrowRight } from "components/icons/ArrowRight";
import { LockedUi } from "components/LockedUi/LockedUi";
import { CASH_FLOW_STATEMENT } from "constants/bookkeeping";
import { AnimatePresence } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { HTMLAttributes, ReactNode, useEffect, useState } from "react";
import ThreeDots from "static/images/ThreeDots.svg";
import { useGetTransactionsForReportQuery } from "store/apis/reports";
import { Account } from "types/Models/report";
import { stopPropagation } from "utils/stopPropagation";

import { CrossIcon } from "components/icons/CrossIcon";
import { ReportsType } from "pages/Books/Reports/Reports";
import { COMMENT_TYPE, CommentType } from "store/apis/notes";
import { ReportFilterType } from "./ReportFilter";
import ConditionalToolTip from "components/design/conditionalToolTip";

export const REPORT_COMMENT_TYPE: Record<ReportsType, CommentType> = {
  BALANCE_SHEET: COMMENT_TYPE.BALANCE_SHEET_COMMENT,
  INCOME_STATEMENT: COMMENT_TYPE.INCOME_STATEMENT_COMMENT,
  CASH_FLOW: COMMENT_TYPE.CASH_FLOW_COMMENT,
  SAVED_REPORTS: COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT,
};

type RootTypes = {
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const Root = ({ children, ...props }: RootTypes) => {
  return <div {...props}>{children}</div>;
};

const Header = ({
  children,
  ...props
}: { children: ReactNode } & React.DetailedHTMLProps<
  HTMLAttributes<HTMLElement>,
  HTMLElement
>) => {
  return (
    <header
      {...props}
      className="t-py-3 t-text-text-30 t-text-body-sm t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-10 t-rounded-t-lg t-flex t-justify-between t-w-full t-gap-24 t-items-center"
    >
      {children}
    </header>
  );
};

export const TransactionsTable = ({
  categoryId,
  startDate,
  endDate,
  accountingMethod,
  isComparisonReport = false,
  transactionsReportHeading,
  handleClose,
  classId,
  merchantId,
  transactionGroupId,
  transaction_group_ids,
  fetch_ungrouped_txn_ids,
  class_ids,
  reportType,
  selectedRows,
  setSelectedRows,
}: {
  categoryId: string;
  startDate?: string;
  endDate?: string;
  accountingMethod?: string;
  isComparisonReport?: boolean;
  transactionsReportHeading?: string;
  handleClose?: () => void;
  classId?: string;
  merchantId?: string;
  transactionGroupId?: string | null;
  reportType: ReportsType;
  selectedRows?: {};
  transaction_group_ids?: string[];
  fetch_ungrouped_txn_ids?: string[];
  class_ids?: string[];
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
}) => {
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const query = useQuery();
  const pageSize = Number(query.get("page_size")) || DEFAULT_PAGE_SIZE;
  const [pageNum, setPageNum] = useState(1);

  // Page size or filters changing invalidates the current page
  useEffect(() => {
    setPageNum(1);
  }, [
    pageSize,
    categoryId,
    startDate,
    endDate,
    accountingMethod,
    classId,
    merchantId,
  ]);

  const {
    data: categoryLedgerInfo,
    isLoading,
    isFetching,
  } = useGetTransactionsForReportQuery(
    {
      entityId: entityId,
      query: {
        accounting_method: accountingMethod,
        start_date: startDate,
        end_date: endDate,
        category_id: categoryId,
        report_type: reportType,
        class_id: classId,
        transaction_group_id: transactionGroupId,
        transaction_group_ids: transaction_group_ids?.join(","),
        fetch_ungrouped_txn_ids: fetch_ungrouped_txn_ids?.join(","),
        class_ids: transactionGroupId ? undefined : class_ids?.join(","),
        merchant_id: merchantId,
        page_num: pageNum,
        page_size: pageSize,
      },
    },
    {
      skip: !categoryId,
    }
  );

  const pagination = (
    <PaginationHandler
      page_num={categoryLedgerInfo?.current_page || 1}
      per_page={categoryLedgerInfo?.per_page || pageSize}
      total_count={categoryLedgerInfo?.total_count || 0}
      total_pages={categoryLedgerInfo?.total_pages || 1}
      onPageNumChange={setPageNum}
      isLoading={isFetching}
      showOptions
    />
  );

  if (isLoading) {
    return (
      <AccordionAnimation>
        <div className="t-flex t-justify-center t-h-full t-w-full t-items-center t-py-3">
          <Loader size="small" />
        </div>
      </AccordionAnimation>
    );
  }

  if (
    categoryLedgerInfo?.transactions &&
    categoryLedgerInfo?.transactions?.length === 0
  ) {
    return (
      <AccordionAnimation>
        {isComparisonReport && (
          <div className="t-flex t-justify-between t-align-middle t-items-center t-pb-4">
            <div className="t-text-body-sm t-font-medium t-text-text-60">
              {transactionsReportHeading}
            </div>
            <Button size="xsmall" customType="ghost_icon" onClick={handleClose}>
              <CrossIcon className="t-h-4 t-w-4" />
            </Button>
          </div>
        )}
        <div className="t-flex t-justify-center t-h-full t-w-full t-items-center t-py-3 t-border-solid t-border-y t-border-0 t-border-neutral-10 t-text-body-sm">
          No transactions found
        </div>
      </AccordionAnimation>
    );
  }

  return (
    <>
      {categoryLedgerInfo?.transactions &&
        categoryLedgerInfo?.transactions?.length > 0 && (
          <AccordionAnimation>
            <div>
              {isComparisonReport ? (
                <div className="t-flex t-justify-between t-align-middle t-items-center t-pb-4">
                  <div className=" t-text-subtext t-text-text-60">
                    {transactionsReportHeading}
                  </div>
                  <div className="t-flex t-items-center t-gap-2">
                    {pagination}
                    <Button
                      size="small"
                      customType="ghost_icon"
                      onClick={handleClose}
                    >
                      <CrossIcon className="t-h-4 t-w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="t-flex t-rounded t-flex-col">
                  <div className="t-text-body-sm t-text-text-60 t-w-full t-flex t-justify-between t-gap-4 t-items-center hover:t-bg-surface-lighter-grey t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-0 t-p-2">
                    <div>
                      {categoryLedgerInfo.show_opening_balance && (
                        <>
                          Starting Balance: {"  "}
                          <AmountSuperScript
                            amount={categoryLedgerInfo?.opening_balance!}
                          />
                        </>
                      )}
                    </div>
                    {pagination}
                  </div>
                </div>
              )}

              <GeneralLedgerTable
                tableData={categoryLedgerInfo?.transactions!}
                reportCategoryType={categoryLedgerInfo?.category_type!}
                startDate={startDate!}
                endDate={endDate!}
                totalDebits={categoryLedgerInfo?.total_debits!}
                totalCredits={categoryLedgerInfo?.total_credits!}
                entityId={entityId}
                showLinkedTxn
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                hasWritePermission={hasWritePermission}
                isHeaderSticky
              />
            </div>
          </AccordionAnimation>
        )}
    </>
  );
};

const Category = ({
  isOpen,
  account,
  nextLevel,
  secondaryAction: action,
  primaryAction,
  openCategory,
  maxIndent,
  isLoading,
  reportType,
  filter,
  selectedRows,
  setSelectedRows,
}: {
  account: Account;
  nextLevel: number;
  secondaryAction?: (args: Account) => ReactNode;
  primaryAction?: (args: Account) => ReactNode;
  isOpen?: boolean;
  openCategory?: string | null;
  maxIndent?: number;
  isLoading?: boolean;
  reportType: ReportsType;
  filter: ReportFilterType;
  selectedRows?: {};
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
}) => {
  const [accordionActive, setAccordionActive] = useState(nextLevel === 1);
  const [showTransactions, setShowTransactions] = useState(false);

  const { uuid, types, name, summary_amount, is_cumulative, identifier } =
    account;

  useEffect(() => {
    if (isOpen !== undefined) {
      setAccordionActive(isOpen);
    }
  }, [isOpen]);

  const handleAmountClick = () => {
    if (identifier && Boolean(summary_amount)) setShowTransactions((v) => !v);
  };

  return (
    <div
      className={classNames("t-w-full", {
        "t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0":
          nextLevel === 1,
      })}
      key={uuid}
    >
      <button
        className={classNames(
          "all:unset t-flex t-justify-between t-w-full hover:t-bg-surface-lighter-grey t-py-2 t-px-3 t-gap-24 sm:t-gap-2 sm:t-pl-0 sm:t-pr-2 t-items-center t-group",
          {
            "animate-transaction-row": openCategory && openCategory === uuid,
          }
        )}
        onClick={(e) => {
          e.stopPropagation();
          setAccordionActive((v) => !v);
        }}
      >
        <div
          className="t-flex t-items-center t-justify-between t-w-full t-gap-2"
          style={{ paddingLeft: types ? 8 * nextLevel : 9 * nextLevel }}
        >
          <div className="t-flex t-items-center">
            <div
              className={classNames({
                "t-block": Boolean(types),
                "t-invisible": !Boolean(types),
              })}
            >
              <div
                className={classNames(
                  "t-transform t-transition t-duration-300 t-ease-in-out",
                  { "t-rotate-90": accordionActive }
                )}
              >
                {types.length > 0 ? (
                  <ArrowRight stroke="1.5" color="currentColor" />
                ) : (
                  <div className="t-w-4"></div>
                )}
              </div>
            </div>
            <div className="t-flex t-w-full t-justify-end t-h-full t-items-center t-gap-3">
              <div
                className={classNames(
                  "t-truncate t-w-max sm:t-max-w-32 t-text-body-sm t-mt-0.5",
                  {
                    "t-text-text-60": showTransactions,
                  }
                )}
              >
                {name}
              </div>
              <div
                className={classNames("t-pb-1", {
                  "t-invisible": nextLevel === 1,
                  "group-hover:!t-visible t-invisible": nextLevel > 1,
                })}
              >
                {action?.(account)}
              </div>
            </div>
          </div>

          {(types.length === 0 || nextLevel > 0) && (
            <button
              className={classNames("all:unset t-text-body-sm t-ml-auto", {
                "hover:!t-underline group-hover:t-text-purple":
                  !showTransactions && identifier && Boolean(summary_amount),
                "!t-underline t-text-purple": showTransactions,
              })}
              onClick={handleAmountClick}
            >
              <AmountSuperScript amount={summary_amount} />
            </button>
          )}

          <AddCommentPopoverRoot contentId={uuid}>
            <Dropdown.Root modal={false}>
              <Dropdown.Trigger
                asChild
                onClick={stopPropagation}
                disabled={!identifier}
              >
                <div>
                  <ConditionalToolTip
                    condition={
                      !identifier && "Action is disabled for non COA item"
                    }
                    side="left"
                  >
                    <span>
                      <AddCommentAnchor contentId={uuid}>
                        <Button
                          disabled={!identifier}
                          customType="ghost_icon"
                          size="small"
                        >
                          <img src={ThreeDots} alt="Action" />
                        </Button>
                      </AddCommentAnchor>
                    </span>
                  </ConditionalToolTip>
                </div>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content onClick={stopPropagation} align="end">
                  <LockedUi feature={WRITE_PERMISSION_KEYS.FINANCIALS}>
                    <Popover.Trigger asChild>
                      <Dropdown.Item>Add comment</Dropdown.Item>
                    </Popover.Trigger>
                  </LockedUi>
                  {Boolean(summary_amount) && (
                    <Dropdown.Item
                      onSelect={() => setShowTransactions((v) => !v)}
                    >
                      {showTransactions ? "Hide" : "Show"} transactions
                    </Dropdown.Item>
                  )}
                </Dropdown.Content>
              </Dropdown.Portal>
              <Popover.Portal>
                <AddCommentPop
                  commentType={REPORT_COMMENT_TYPE[reportType]}
                  contentId={uuid}
                />
              </Popover.Portal>
            </Dropdown.Root>
          </AddCommentPopoverRoot>
        </div>
      </button>

      {showTransactions && (
        <AnimatePresence>
          <div style={{ paddingLeft: 9 * nextLevel + 26 }}>
            <TransactionsTable
              accountingMethod={filter.ACCOUNTING_METHOD}
              startDate={filter.START_DATE}
              endDate={filter.END_DATE}
              reportType={reportType}
              categoryId={uuid}
              fetch_ungrouped_txn_ids={filter.NO_CLASSIFICATION}
              transaction_group_ids={filter.CLASSIFICATION}
              class_ids={filter.CLASS_IDS}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </div>
        </AnimatePresence>
      )}

      <AnimatePresence>
        {accordionActive && types && (
          <AccordionAnimation>
            <div
              className={classNames({
                "t-text-text-100": nextLevel === 0,
                "t-text-text-60": nextLevel === 1,
                "t-text-text-30": nextLevel >= 2,
              })}
            >
              <Trigger
                openCategory={openCategory}
                data={types}
                level={nextLevel}
                secondaryAction={action}
                primaryAction={primaryAction}
                maxIndent={maxIndent}
                isLoading={isLoading}
                reportType={reportType}
                filter={filter}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            </div>
          </AccordionAnimation>
        )}
      </AnimatePresence>

      {is_cumulative && types.length > 0 && accordionActive && (
        <div
          className="t-w-full t-flex t-justify-between t-border t-border-b-0 t-border-solid t-border-t t-border-x-0 t-border-neutral-0 t-text-text-100 hover:t-bg-surface-lighter-grey t-py-2 t-px-3 sm:t-pl-0 sm:t-pr-2"
          style={{
            paddingLeft: types ? 8 * nextLevel : 9 * nextLevel,
          }}
        >
          {" "}
          <div className="t-min-w-32 t-truncate t-w-max t-text-body-sm t-mt-0.5 t-pl-6">
            {nextLevel === 1 && reportType === CASH_FLOW_STATEMENT
              ? "Net cash provided by"
              : "Total"}{" "}
            {name}
          </div>
          <div className="t-text-body-sm t-flex t-gap-2">
            <AmountSuperScript amount={summary_amount} />
            <div className="t-pointer-events-none t-opacity-0 t-invisible">
              <div className="t-w-8 t-h-8" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Trigger = ({
  data,
  level = 0,
  secondaryAction,
  primaryAction,
  children,
  openCategory,
  maxIndent = 4,
  isLoading,
  reportType,
  filter,
  selectedRows,
  setSelectedRows,
}: {
  data: Account[];
  level?: number;
  secondaryAction?: (props: Account) => ReactNode;
  primaryAction?: (props: Account) => ReactNode;
  reportType: ReportsType;
  children?: ReactNode;
  openCategory?: string | null;
  maxIndent?: number;
  isLoading?: boolean;
  filter: ReportFilterType;
  selectedRows?: {};
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
}) => {
  const [open, setOpen] = useState<string[]>([]);

  useEffect(() => {
    const getParentTree = (
      ledger: Account[],
      selectedCategoryId: string | null,
      currentParents: string[] = []
    ): string[] | undefined => {
      for (const category of ledger) {
        if (category.uuid === selectedCategoryId) {
          return currentParents;
        }

        if (category.types) {
          const parent = getParentTree(category.types, selectedCategoryId, [
            category.uuid,
            ...currentParents,
          ]);
          if (parent?.length) {
            return parent;
          }
        }
      }
    };

    if (openCategory) {
      const parents = getParentTree(data, openCategory);
      if (parents && parents.length > 0) {
        setOpen(parents);
      }
    }
  }, [data, openCategory]);

  let nextLevel = level;
  if (maxIndent && nextLevel <= maxIndent) {
    nextLevel = nextLevel + 1;
  }

  if (!data) {
    return null;
  }

  if (isLoading) {
    return (
      <>
        {data.map(({ uuid }) => (
          <div
            className="t-w-full t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0 t-flex t-justify-between t-py-2 t-px-3 sm:t-pl-0 sm:t-pr-2 t-h-11 t-items-center"
            key={uuid}
          >
            <div className="t-w-2/4 t-bg-neutral-10 t-animate-pulse t-h-5 t-rounded"></div>
            <div className="t-w-1/6  t-bg-neutral-10 t-animate-pulse t-h-5 t-rounded"></div>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {data?.map((account) => (
        <Category
          isOpen={open.includes(account.uuid)}
          openCategory={openCategory}
          key={account.uuid}
          account={account}
          nextLevel={nextLevel}
          secondaryAction={secondaryAction}
          primaryAction={primaryAction}
          maxIndent={maxIndent}
          isLoading={isLoading}
          reportType={reportType}
          filter={filter}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      ))}
    </>
  );
};

const ReportsAccordion = {
  Root,
  Trigger,
  Header,
};

export default ReportsAccordion;
